import {
  AGREEMENT_GROUPS,
  AGREEMENT_ITEM_IDS_FIELD,
  AGREEMENT_ITEM_QUANTITIES_FIELD,
  getAllFieldsByType,
} from "constants/AgreementReportConstant";
import { TYPE_REPORT_WORD } from "constants/IssueInvoiceConstant";
import moment from "moment";
import * as yup from "yup";

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Tách tên field phẳng thành đường dẫn lồng nhau + key con.
 * Phải so khớp theo prefix vì tên nhóm cũng chứa dấu gạch dưới (party_a_before).
 */
export const splitFlatKey = flatKey => {
  const group = AGREEMENT_GROUPS.find(item =>
    flatKey.startsWith(`${item.prefix}_`)
  );
  if (!group) {
    return { path: [], key: flatKey };
  }
  return { path: group.path, key: flatKey.slice(group.prefix.length + 1) };
};

const readRawValue = (data, flatKey) => {
  const { path, key } = splitFlatKey(flatKey);
  const parent = path.reduce(
    (result, step) => (result ? result[step] : undefined),
    data
  );
  return parent ? parent[key] : undefined;
};

/** Đi theo path, tạo object rỗng cho nhánh nào chưa có */
const ensurePath = (root, path) =>
  path.reduce((result, step) => {
    if (!result[step] || typeof result[step] !== "object") {
      result[step] = {};
    }
    return result[step];
  }, root);

const isEmptyValue = value =>
  value === null || value === undefined || value === "";

/**
 * Biên bản lập lần đầu (chưa có agreement_id) thì phần "cần điều chỉnh"
 * mặc định lấy theo giá trị hiện tại của hóa đơn — áp dụng cho mọi nhóm
 * có cặp before/after (amounts, party_a).
 *
 * Riêng biên bản trả hàng: không cho sửa gì nên số liệu luôn bám theo hóa đơn,
 * mà API để trống nhóm after -> phải lấy before kể cả khi biên bản đã lập,
 * nếu không 4 ô "Số tiền trả hàng" sẽ ra 0.
 */
const readValueWithFallback = (data, flatKey, type) => {
  const raw = readRawValue(data, flatKey);
  const isAlwaysFromInvoice = type === TYPE_REPORT_WORD.GRA;

  if (!isEmptyValue(raw) || (data?.agreement_id && !isAlwaysFromInvoice)) {
    return raw;
  }

  const beforeKey = flatKey.replace("_after_", "_before_");
  return beforeKey === flatKey ? raw : readRawValue(data, beforeKey);
};

/**
 * Id các dòng hàng đang giữ lại của biên bản trả hàng.
 * API luôn trả đủ items để người dùng khôi phục dòng đã xóa, còn selected_ids
 * là các dòng đang giữ. Biên bản chưa từng chọn thì giữ hết.
 */
export const buildSelectedItemIds = data => {
  const items = Array.isArray(data?.items) ? data.items : [];
  const allIds = items
    .map(item => Number(item?.id))
    .filter(id => Number.isFinite(id));

  if (!Array.isArray(data?.selected_ids)) {
    return allIds;
  }

  // Chỉ nhận id có thật trong items, giữ đúng thứ tự dòng của hóa đơn
  const selected = data.selected_ids.map(Number);
  return allIds.filter(id => selected.includes(id));
};

/**
 * Số lượng trả của từng dòng: { [id]: số lượng }. Bản mới lấy số lượng
 * trên hóa đơn, bản đã lưu lấy số đã lưu (agreement-info trả sẵn trong quantity).
 */
export const buildItemQuantities = data => {
  const quantities = {};

  (Array.isArray(data?.items) ? data.items : []).forEach(item => {
    quantities[Number(item.id)] = Number(item.quantity);
  });

  return quantities;
};

/**
 * Lỗi của số lượng trả, rỗng nếu hợp lệ. Giới hạn trên do BE kiểm. Dòng có số
 * lượng ban đầu là 0 / rỗng (vd. ghi chú) thì được để 0.
 */
export const validateReturnQuantity = (value, initialQuantity) => {
  const quantity = Number(value);

  if (Number(initialQuantity || 0) === 0 && (value === "" || quantity === 0)) {
    return "";
  }

  if (value === "" || !Number.isInteger(quantity) || quantity < 1) {
    return "Số lượng trả phải là số nguyên lớn hơn 0";
  }

  return "";
};

/**
 * items[{id, quantity}] gửi agreement-gra-preview / agreement-save: các dòng
 * đang giữ.
 */
export const buildReturnItems = (items, keptIds, quantities) => {
  const kept = (keptIds || []).map(Number);

  return (Array.isArray(items) ? items : [])
    .filter(item => kept.includes(Number(item?.id)))
    .map(item => ({
      id: Number(item.id),
      quantity: Number(quantities?.[Number(item.id)] ?? item.quantity),
    }));
};

/** Field chỉ thuộc về một bên (bên mua / bên bán) thì bên kia không dùng tới */
const isFieldOfAdjustParty = (item, adjustParty) =>
  !item.visibleWhen ||
  String(adjustParty || "") === String(item.visibleWhen.is);

/** JSON lồng nhau của API -> giá trị phẳng cho react-hook-form */
export const apiToFormValues = (data, type) => {
  const values = {};

  getAllFieldsByType(type).forEach(item => {
    const raw = readValueWithFallback(data, item.field, type);

    switch (item.type) {
      case "toggleField":
        // Biên bản mới chưa có giá trị -> dùng mặc định của field (vd "buyer")
        values[item.field] = isEmptyValue(raw)
          ? item.defaultValue || ""
          : `${raw}`;
        break;
      case "date":
        values[item.field] = raw ? [moment(raw, DATE_FORMAT).toDate()] : [];
        break;
      case "number":
        values[item.field] =
          raw === null || raw === undefined || raw === "" ? 0 : Number(raw);
        break;
      case "radioField":
        // Lập lần đầu API vẫn trả sẵn giá trị mặc định (vd loại bên mua = cá
        // nhân) -> bỏ qua để người lập tự chọn; đã lập thì giữ lựa chọn đã lưu
        values[item.field] =
          !data?.agreement_id || isEmptyValue(raw) ? "" : `${raw}`;
        break;
      default:
        values[item.field] = raw === null || raw === undefined ? "" : `${raw}`;
        break;
    }
  });

  // Dòng hàng giữ lại của biên bản trả hàng đi kèm giá trị form, không phải field
  values[AGREEMENT_ITEM_IDS_FIELD] = buildSelectedItemIds(data);
  values[AGREEMENT_ITEM_QUANTITIES_FIELD] = buildItemQuantities(data);

  return values;
};

/** Giá trị phẳng của form -> JSON lồng nhau đúng shape API */
export const formValuesToApi = (values, rawData, type) => {
  // Nhân bản sâu để không sửa vào rawData đang giữ trong state của modal.
  // Dữ liệu biên bản thuần JSON nên cách này an toàn.
  const payload = JSON.parse(JSON.stringify(rawData || {}));
  const adjustParty = values?.adjust_party;

  getAllFieldsByType(type).forEach(item => {
    // Đang điều chỉnh bên nào thì chỉ gửi ô nhập của bên đó. Field không gửi
    // thì BE giữ nguyên giá trị đã lưu.
    if (!isFieldOfAdjustParty(item, adjustParty)) {
      return;
    }

    const { path, key } = splitFlatKey(item.field);
    const value = values[item.field];
    let finalValue;

    switch (item.type) {
      case "date":
        finalValue =
          Array.isArray(value) && value.length > 0
            ? moment(value[0]).format(DATE_FORMAT)
            : "";
        break;
      case "number":
        finalValue = `${Math.trunc(Number(value || 0))}`;
        break;
      default:
        finalValue = value === null || value === undefined ? "" : `${value}`;
        break;
    }

    ensurePath(payload, path)[key] = finalValue;
  });

  // Biên bản trả hàng: BE chỉ nhận id dòng giữ lại rồi tự tính lại số tiền
  if (Array.isArray(values?.[AGREEMENT_ITEM_IDS_FIELD])) {
    payload[AGREEMENT_ITEM_IDS_FIELD] =
      values[AGREEMENT_ITEM_IDS_FIELD].map(Number);
    // items của rawData còn số lượng cũ, BE lại đọc số lượng từ đây -> thay
    // bằng đúng số lượng đang nhập
    payload.items = buildReturnItems(
      rawData?.items,
      values[AGREEMENT_ITEM_IDS_FIELD],
      values[AGREEMENT_ITEM_QUANTITIES_FIELD]
    );
  }

  return payload;
};

/**
 * Giới hạn số ký tự. Phải gắn TRƯỚC applyRequired: nhánh when của yup 0.32 thay
 * thế cả schema, nên ràng buộc gắn sau sẽ mất khi điều kiện khớp.
 */
const withMax = (schema, item, t) =>
  item.max
    ? schema.max(item.max, t("form.validate.char_max", { max: item.max }))
    : schema;

/**
 * Gắn ràng buộc bắt buộc cho một field.
 * required: luôn bắt buộc. requiredWhen: chỉ bắt buộc khi field khác mang đúng
 * giá trị (vd ô của bên bán chỉ bắt buộc khi đang điều chỉnh thông tin bên bán).
 */
const applyRequired = (schema, item, message) => {
  if (item.required) {
    return schema.required(message);
  }

  if (!item.requiredWhen) {
    return schema;
  }

  return schema.when(item.requiredWhen.field, {
    is: item.requiredWhen.is,
    then: schema.required(message),
  });
};

/** Dựng yup schema + defaultValues từ config field (theo khuôn DetailData.js) */
export const buildFormSchema = (fields, t) => {
  const objSchema = {};
  const defaultValues = {};

  fields.forEach(item => {
    // Nhãn phải lấy theo labelKey giống lúc render, nếu không câu báo lỗi sẽ ra
    // nguyên tên field (vd "Vui lòng nhập form.amounts_after_amount")
    const title = t(`form.${item.labelKey || item.field}`);

    switch (item.type) {
      case "number":
        defaultValues[item.field] = 0;
        objSchema[item.field] = applyRequired(
          yup.number().transform(value => (isNaN(value) ? undefined : value)),
          item,
          t("form.validate.required", { title })
        );
        break;
      case "date":
        defaultValues[item.field] = [];
        objSchema[item.field] = yup.array();
        if (item.required) {
          objSchema[item.field] = objSchema[item.field].min(
            1,
            t("form.validate.select", { title })
          );
        }
        break;
      case "email": {
        defaultValues[item.field] = "";
        // yup bỏ qua chuỗi rỗng nên ô không bắt buộc vẫn để trống được
        const emailSchema = withMax(
          yup.string().email(t("form.validate.invalid", { title })),
          item,
          t
        );
        objSchema[item.field] = applyRequired(
          emailSchema,
          item,
          t("form.validate.required", { title })
        );
        break;
      }
      case "radioField":
      case "toggleField":
        // Radio không chọn sẵn gì; toggle có thể có giá trị mặc định riêng.
        // Nhóm radio chưa tick nào thì react-hook-form trả null -> nullable để
        // yup không báo lỗi kiểu, required bên dưới vẫn chặn null
        defaultValues[item.field] = item.defaultValue || "";
        objSchema[item.field] = applyRequired(
          yup.string().nullable(),
          item,
          t("form.validate.select", { title })
        );
        break;
      default:
        defaultValues[item.field] = "";
        objSchema[item.field] = applyRequired(
          withMax(yup.string(), item, t),
          item,
          t("form.validate.required", { title })
        );
        break;
    }
  });

  return { schema: yup.object().shape(objSchema), defaultValues };
};
